// A solution's person-scoped settings under *Your credentials*
// (02-DESIGN/14-solutions/01-the-manifest.md § settings and secrets): each
// lives at `person.<principal>.solution.<name>.<setting>` in the workspace's
// vault, the person's own namespace, written over their own connection. The
// page shows that a value is set, offers to replace or remove it, and never
// shows it: the console cannot read it back.

import { useState } from "react";

import { Button, Confirm, NotAllowed, TextInput, useLoad, when } from "../../ui/components.tsx";
import { personPrefix, personSettingOf, personSettingPath } from "../../wire/solutions.ts";
import { useSurface } from "../surface.tsx";
import { useInstalled } from "./installed.ts";
import { personSettings } from "./words.ts";

export function PersonSettings() {
  const s = useSurface();
  const installed = useInstalled(s.bus);
  const principal = s.principal;
  const wanted = installed.flatMap((r) =>
    r.manifest === undefined
      ? []
      : personSettings(r.manifest).map((x) => ({
          solution: r.name,
          display: r.manifest?.display ?? r.name,
          setting: x,
        })),
  );
  const [tick, setTick] = useState(0);
  const [held] = useLoad(
    `credentials/solutions/${principal}/${String(wanted.length)}`,
    () =>
      wanted.length === 0 || principal === ""
        ? Promise.resolve(new Map<string, string>())
        : s.bus
            .vault()
            .list(personPrefix(principal))
            .then((reply) => {
              const out = new Map<string, string>();
              for (const e of reply.paths) {
                const at = personSettingOf(e.path);
                if (at !== undefined && e.version > 0) {
                  out.set(`${at.solution}/${at.setting}`, e.stored_at);
                }
              }
              return out;
            }),
    tick,
  );
  const [typed, setTyped] = useState<Record<string, string>>({});
  const [err, setErr] = useState<unknown>(undefined);
  if (wanted.length === 0 || principal === "") {
    return null;
  }
  const done = () => {
    setTick((t) => t + 1);
  };
  return (
    <section className="section">
      <div className="section-head">
        <h2>Solutions</h2>
        <span className="small faint">what each solution reads as you; never shown back</span>
      </div>
      {err !== undefined && <NotAllowed err={err} />}
      {held.state === "refused" && <NotAllowed err={held.err} what="Your settings for solutions" />}
      <div className="card stack">
        {wanted.map((w) => {
          const k = `${w.solution}/${w.setting.name}`;
          const stored = held.state === "ready" ? held.value.get(k) : undefined;
          const path = personSettingPath(principal, w.solution, w.setting.name);
          return (
            <div className="row" key={k}>
              <span className="grow">
                <span className="cell-main">
                  {w.display}: {w.setting.label}
                </span>
                <span className="cell-sub">
                  {stored !== undefined ? `set ${when(stored)}` : "not set"}
                  {w.setting.hint !== undefined && ` · ${w.setting.hint}`}
                </span>
              </span>
              <TextInput
                type={w.setting.secret === true ? "password" : "text"}
                value={typed[k] ?? ""}
                onChange={(v) => {
                  setTyped({ ...typed, [k]: v });
                }}
              />
              <Button
                size="sm"
                disabled={(typed[k] ?? "") === ""}
                onClick={() => {
                  setErr(undefined);
                  s.bus
                    .vault()
                    .store(path, typed[k] ?? "")
                    .then(
                      () => {
                        setTyped({ ...typed, [k]: "" });
                        done();
                      },
                      (e: unknown) => {
                        setErr(e);
                      },
                    );
                }}
              >
                {stored !== undefined ? "Replace" : "Store"}
              </Button>
              {stored !== undefined && (
                <Confirm
                  label="Remove"
                  consequence={`${w.display} no longer has it.`}
                  onConfirm={() => {
                    s.bus
                      .vault()
                      .delete(path, true)
                      .then(done, (e: unknown) => {
                        setErr(e);
                      });
                  }}
                />
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
